import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Upload, CheckCircle, Clock, AlertTriangle,
  DollarSign, FileText, MapPin, Calendar, Play, CheckCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import StatusBadge from '../components/common/StatusBadge';
import ProgressBar from '../components/common/ProgressBar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertBanner from '../components/common/AlertBanner';

const MilestoneTrackerPage = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const userStr = localStorage.getItem('xchange_user');
      const user = JSON.parse(userStr);
      setUserRole(user.role);

      // Fetch project details
      const projectRes = await fetch(`${apiUrl}/api/projects/${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const projectData = await projectRes.json();
      setProject(projectData);

      // Fetch milestones
      const milestonesRes = await fetch(`${apiUrl}/api/milestones?projectId=${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const milestonesData = await milestonesRes.json();
      setMilestones(milestonesData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleStartMilestone = async (milestoneId) => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      await fetch(`${apiUrl}/api/milestones/${milestoneId}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      fetchData();
    } catch (error) {
      console.error('Error starting milestone:', error);
    }
  };

  const handleCompleteMilestone = async (milestoneId) => {
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      await fetch(`${apiUrl}/api/milestones/${milestoneId}/complete`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ remarks: 'Milestone completed and submitted for verification' })
      });

      fetchData();
    } catch (error) {
      console.error('Error completing milestone:', error);
    }
  };

  if (loading) {
    return <LoadingSpinner fullScreen size="xl" />;
  }

  // Group milestones by status
  const pendingMilestones = milestones.filter(m => m.status === 'pending');
  const inProgressMilestones = milestones.filter(m => m.status === 'in_progress');
  const completedMilestones = milestones.filter(m => m.status === 'completed');
  const delayedMilestones = milestones.filter(m => m.status === 'delayed');

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/projects/${projectId}`)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Project
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Milestone Tracker</h1>
              <p className="text-gray-600 mt-1">{project?.title}</p>
            </div>
            <StatusBadge status={project?.status} />
          </div>
        </div>

        {/* Project Summary */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <div className="grid grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Total Milestones</p>
              <p className="text-2xl font-bold text-gray-900">{milestones.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Completed</p>
              <p className="text-2xl font-bold text-green-600">{completedMilestones.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">In Progress</p>
              <p className="text-2xl font-bold text-blue-600">{inProgressMilestones.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Completion</p>
              <p className="text-2xl font-bold text-teal-600">{project?.completionPercentage?.toFixed(0)}%</p>
            </div>
          </div>
        </div>

        {/* Kanban Board */}
        <div className="grid grid-cols-4 gap-6">
          {/* Pending Column */}
          <MilestoneColumn
            title="Pending"
            count={pendingMilestones.length}
            color="gray"
            milestones={pendingMilestones}
            onMilestoneClick={setSelectedMilestone}
            onAction={handleStartMilestone}
            actionLabel="Start"
            actionIcon={Play}
            userRole={userRole}
          />

          {/* In Progress Column */}
          <MilestoneColumn
            title="In Progress"
            count={inProgressMilestones.length}
            color="blue"
            milestones={inProgressMilestones}
            onMilestoneClick={setSelectedMilestone}
            onAction={(id) => {
              setSelectedMilestone(milestones.find(m => m.id === id));
              setShowUploadModal(true);
            }}
            actionLabel="Upload Evidence"
            actionIcon={Upload}
            userRole={userRole}
          />

          {/* Completed Column */}
          <MilestoneColumn
            title="Completed"
            count={completedMilestones.length}
            color="green"
            milestones={completedMilestones}
            onMilestoneClick={setSelectedMilestone}
            userRole={userRole}
          />

          {/* Delayed Column */}
          <MilestoneColumn
            title="Delayed"
            count={delayedMilestones.length}
            color="red"
            milestones={delayedMilestones}
            onMilestoneClick={setSelectedMilestone}
            userRole={userRole}
          />
        </div>

        {/* Milestone Detail Modal */}
        {selectedMilestone && (
          <MilestoneDetailModal
            milestone={selectedMilestone}
            onClose={() => setSelectedMilestone(null)}
            onComplete={handleCompleteMilestone}
            userRole={userRole}
          />
        )}

        {/* Evidence Upload Modal */}
        {showUploadModal && selectedMilestone && (
          <EvidenceUploadModal
            milestone={selectedMilestone}
            onClose={() => {
              setShowUploadModal(false);
              fetchData();
            }}
          />
        )}
      </div>
    </div>
  );
};

// Kanban Column Component
const MilestoneColumn = ({ title, count, color, milestones, onMilestoneClick, onAction, actionLabel, actionIcon: ActionIcon, userRole }) => {
  const colorClasses = {
    gray: 'bg-gray-100 text-gray-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700'
  };

  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <div className={`${colorClasses[color]} rounded-lg px-3 py-2 mb-4`}>
        <div className="flex items-center justify-between">
          <span className="font-semibold">{title}</span>
          <span className="text-sm font-bold">{count}</span>
        </div>
      </div>

      <div className="space-y-3">
        {milestones.map(milestone => (
          <div
            key={milestone.id}
            className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => onMilestoneClick(milestone)}
          >
            <h4 className="font-semibold text-gray-900 mb-2">{milestone.title}</h4>
            <p className="text-xs text-gray-500 mb-3">Milestone #{milestone.milestoneNumber}</p>
            
            <ProgressBar
              progress={milestone.completionPercentage}
              showLabel={false}
              height="h-1"
            />

            <div className="mt-3 space-y-2 text-xs text-gray-600">
              <div className="flex items-center justify-between">
                <span>Budget:</span>
                <span className="font-semibold">₹{(milestone.budgetAllocated / 100000).toFixed(1)}L</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Evidence:</span>
                <span className="font-semibold">{milestone.evidence?.length || 0} items</span>
              </div>
            </div>

            {onAction && userRole === 'ngo' && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  onAction(milestone.id);
                }}
                size="sm"
                className="w-full mt-3 text-white"
                style={{ background: '#14B8A6' }}
              >
                {ActionIcon && <ActionIcon className="w-3 h-3 mr-1" />}
                {actionLabel}
              </Button>
            )}
          </div>
        ))}

        {milestones.length === 0 && (
          <div className="text-center py-8 text-gray-400">
            <p className="text-sm">No milestones</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Milestone Detail Modal
const MilestoneDetailModal = ({ milestone, onClose, onComplete, userRole }) => {
  const getDaysUntilDue = () => {
    const due = new Date(milestone.expectedCompletionDate);
    const now = new Date();
    const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const daysUntil = getDaysUntilDue();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">{milestone.title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <span className="text-2xl">×</span>
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label className="text-sm text-gray-500">Status</label>
              <div className="mt-1">
                <StatusBadge status={milestone.status} />
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-500">Progress</label>
              <div className="mt-2">
                <ProgressBar progress={milestone.completionPercentage} label="" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Budget Allocated</p>
              <p className="text-xl font-bold text-gray-900">₹{(milestone.budgetAllocated / 100000).toFixed(2)}L</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Funds Released</p>
              <p className="text-xl font-bold text-green-600">₹{(milestone.fundsReleased / 100000).toFixed(2)}L</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-500 mb-1">Funds Utilized</p>
              <p className="text-xl font-bold text-blue-600">₹{(milestone.fundsUtilized / 100000).toFixed(2)}L</p>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Description</h3>
            <p className="text-gray-700">{milestone.description}</p>
          </div>

          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Deliverables</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              {milestone.deliverables?.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Timeline</h3>
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="text-gray-500">Expected Completion:</span>
                <span className="ml-2 font-semibold">{new Date(milestone.expectedCompletionDate).toLocaleDateString()}</span>
              </div>
              {daysUntil < 0 ? (
                <span className="text-red-600 font-semibold">({Math.abs(daysUntil)} days overdue)</span>
              ) : (
                <span className="text-gray-600">({daysUntil} days remaining)</span>
              )}
            </div>
          </div>

          {milestone.evidence && milestone.evidence.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Evidence ({milestone.evidence.length})</h3>
              <div className="grid grid-cols-3 gap-4">
                {milestone.evidence.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3">
                    <p className="text-sm font-semibold text-gray-900 mb-1">{item.type}</p>
                    <p className="text-xs text-gray-500">{item.caption || 'No caption'}</p>
                    <p className="text-xs text-gray-400 mt-2">{new Date(item.uploadedAt).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {userRole === 'ngo' && milestone.status === 'in_progress' && milestone.completionPercentage >= 90 && (
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  onComplete(milestone.id);
                  onClose();
                }}
                className="text-white"
                style={{ background: '#10B981' }}
              >
                <CheckCheck className="w-5 h-5 mr-2" />
                Submit for Verification
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Evidence Upload Modal
const EvidenceUploadModal = ({ milestone, onClose }) => {
  const [evidenceType, setEvidenceType] = useState('photo');
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('xchange_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('evidence_type', evidenceType);
      formData.append('caption', caption);

      await fetch(`${apiUrl}/api/milestones/${milestone.id}/evidence`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      onClose();
    } catch (error) {
      console.error('Error uploading evidence:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Upload Evidence</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Evidence Type</label>
            <select
              value={evidenceType}
              onChange={(e) => setEvidenceType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            >
              <option value="photo">Photo</option>
              <option value="video">Video</option>
              <option value="document">Document</option>
              <option value="report">Report</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Caption</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Describe this evidence..."
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">File</label>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              className="w-full"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            className="flex-1 text-white"
            style={{ background: '#14B8A6' }}
            disabled={!file || uploading}
          >
            {uploading ? <LoadingSpinner size="sm" /> : 'Upload'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MilestoneTrackerPage;
